import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Wallet, Clock, TrendingUp, CalendarDays, ChevronLeft, ChevronRight, BarChart3, Utensils } from "lucide-react";
import { MOIS, jourLong } from "@/lib/dates";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const TYPE_LABELS = {
  travaille: "Travaillé",
  conge_paye: "Congé payé",
  conge: "Congé",
  absence_maladie: "Maladie",
  jour_ferie: "Jour férié",
  repos: "Repos",
};

export default function SalarieRemuneration() {
  const now = new Date();
  const [mois, setMois] = useState(now.getMonth() + 1);
  const [annee, setAnnee] = useState(now.getFullYear());
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [r, h] = await Promise.all([
        api.get("/salarie/remuneration", { params: { mois, annee } }),
        api.get("/salarie/remuneration/history"),
      ]);
      setData(r.data);
      setHistory(h.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [mois, annee]);

  const shift = (dir) => {
    let m = mois + dir;
    let y = annee;
    if (m < 1) { m = 12; y--; }
    if (m > 12) { m = 1; y++; }
    setMois(m); setAnnee(y);
  };

  const isCurrentMonth = mois === now.getMonth() + 1 && annee === now.getFullYear();

  return (
    <div className="min-h-full">
      <PageHeader
        eyebrow="Rémunération"
        title="Ma rémunération"
        sub="Aperçu temps réel de vos heures cumulées et de votre salaire brut mensuel calculé."
        right={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => shift(-1)} data-testid="remu-prev" className="h-10"><ChevronLeft className="h-4 w-4" /></Button>
            <Select value={String(mois)} onValueChange={(v) => setMois(Number(v))}>
              <SelectTrigger data-testid="remu-mois" className="w-36 h-10"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MOIS.map((m, i) => <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={String(annee)} onValueChange={(v) => setAnnee(Number(v))}>
              <SelectTrigger data-testid="remu-annee" className="w-24 h-10"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map((y) => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={() => shift(1)} data-testid="remu-next" className="h-10"><ChevronRight className="h-4 w-4" /></Button>
          </div>
        }
      />

      <div className="p-6 lg:p-10 space-y-6">
        {/* Hero brut */}
        <div className="bg-[#002FA7] text-white p-8 lg:p-12 border border-[#002FA7]" data-testid="remu-hero">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] font-semibold opacity-80 mb-4">
            <Wallet className="h-4 w-4" /> Salaire brut estimé — {MOIS[mois - 1]} {annee}
            {isCurrentMonth && <span className="ml-2 px-2 py-0.5 bg-[#FFD700] text-[#0A0A0A] text-[10px]">EN COURS</span>}
          </div>
          <div className="font-display text-6xl lg:text-7xl font-black tracking-tighter" data-testid="remu-brut">
            {loading ? "…" : data ? `${data.brut_calcule.toFixed(2)} €` : "—"}
          </div>
          {data && (
            <div className="mt-4 flex flex-wrap gap-8 text-sm">
              <div><span className="opacity-70">Heures travaillées :</span> <span className="font-bold">{data.total_heures.toFixed(2)} h</span></div>
              <div><span className="opacity-70">Taux horaire :</span> <span className="font-bold">{data.taux_horaire.toFixed(2)} €/h</span></div>
              <div><span className="opacity-70">Jours travaillés :</span> <span className="font-bold">{data.jours_travailles}</span></div>
            </div>
          )}
          <div className="mt-6 pt-6 border-t border-white/20 text-xs opacity-70 max-w-xl">
            Montant indicatif calculé sur la base de votre planning validé × taux horaire. Ne remplace pas le bulletin de paie officiel déposé par votre employeur.
          </div>
        </div>

        {/* Breakdown */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="remu-breakdown">
          <BreakBox icon={Clock} label="Heures" value={data?.breakdown.heures_travaillees?.toFixed(2) ?? "—"} unit="h" />
          <BreakBox icon={CalendarDays} label="Congés payés" value={data?.breakdown.jours_conges_paye ?? "—"} unit="j" />
          <BreakBox icon={CalendarDays} label="Sans solde" value={data?.breakdown.jours_conge_sans_solde ?? "—"} unit="j" />
          <BreakBox icon={CalendarDays} label="Repos" value={data?.breakdown.jours_repos ?? "—"} unit="j" />
          <BreakBox icon={CalendarDays} label="Jours fériés" value={data?.breakdown.jours_feries ?? "—"} unit="j" />
          <BreakBox icon={CalendarDays} label="Maladie" value={data?.breakdown.jours_maladie ?? "—"} unit="j" />
        </div>

        {data?.titres_restaurant?.enabled && (
          <div className="bg-white border border-[#DEE2E6] p-5 flex flex-wrap items-center gap-6" data-testid="remu-tr">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-semibold text-[#495057]"><Utensils className="h-4 w-4 text-[#002FA7]" /> Titres-restaurant</div>
            <div><div className="text-[10px] uppercase tracking-wider text-[#868E96]">Titres acquis</div><div className="font-display font-black text-2xl" data-testid="remu-tr-tickets">{data.titres_restaurant.tickets}</div></div>
            <div><div className="text-[10px] uppercase tracking-wider text-[#868E96]">Valeur faciale</div><div className="font-display font-black text-2xl">{Number(data.titres_restaurant.amounts?.valeur_faciale || 0).toFixed(2)} €</div></div>
            <div><div className="text-[10px] uppercase tracking-wider text-[#868E96]">Part employeur</div><div className="font-display font-black text-2xl text-[#059669]">{Number(data.titres_restaurant.part_employeur || 0).toFixed(2)} €</div></div>
            <div><div className="text-[10px] uppercase tracking-wider text-[#868E96]">Retenue sur salaire</div><div className="font-display font-black text-2xl text-[#E60000]" data-testid="remu-tr-part">-{Number(data.titres_restaurant.part_salarie || 0).toFixed(2)} €</div></div>
            <div className="text-[11px] text-[#868E96] basis-full">1 titre par journée travaillée d'au moins 5 h englobant la pause repas · {data.titres_restaurant.validated ? "mois validé par l'employeur" : "estimation en direct"}</div>
          </div>
        )}

        {/* Chart 12 derniers mois */}
        <div className="bg-white border border-[#DEE2E6]" data-testid="remu-chart">
          <div className="p-4 border-b border-[#DEE2E6] flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-[#002FA7]" />
            <div className="text-xs uppercase tracking-[0.25em] font-semibold">Évolution brut — 12 derniers mois</div>
          </div>
          <div className="p-4 h-72">
            {history.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={history} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="brutGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#002FA7" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#002FA7" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="0" stroke="#DEE2E6" vertical={false} />
                  <XAxis dataKey="label" stroke="#868E96" fontSize={11} tickLine={false} axisLine={{ stroke: "#DEE2E6" }} />
                  <YAxis stroke="#868E96" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `${v}€`} />
                  <Tooltip
                    contentStyle={{ background: "#0A0A0A", border: "none", borderRadius: 0, color: "white", fontSize: 12 }}
                    labelStyle={{ color: "#FFD700", textTransform: "uppercase", letterSpacing: "0.15em", fontSize: 10, fontWeight: 600 }}
                    formatter={(v, n) => [`${Number(v).toFixed(2)} €`, "Brut"]}
                  />
                  <Area type="monotone" dataKey="brut_calcule" stroke="#002FA7" strokeWidth={2} fill="url(#brutGradient)" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-sm text-[#868E96]">Chargement…</div>
            )}
          </div>
        </div>

        {/* Details */}
        <div className="bg-white border border-[#DEE2E6]">
          <div className="p-4 border-b border-[#DEE2E6] flex items-center justify-between">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-[#002FA7]" />
              <div className="text-xs uppercase tracking-[0.25em] font-semibold">Détail des jours</div>
            </div>
            <div className="text-xs text-[#868E96]">{data?.details?.length || 0} entrée(s)</div>
          </div>
          <div className="grid grid-cols-12 gap-4 p-4 text-xs uppercase tracking-[0.2em] font-semibold bg-[#F8F9FA] border-b border-[#DEE2E6]">
            <div className="col-span-4">Date</div>
            <div className="col-span-3">Type</div>
            <div className="col-span-3">Horaires</div>
            <div className="col-span-2 text-right">Heures</div>
          </div>
          {data?.details?.length ? data.details.map((d, i) => (
            <div key={i} className="grid grid-cols-12 gap-4 p-4 border-b border-[#DEE2E6] items-center text-sm last:border-b-0" data-testid={`remu-detail-${d.date}`}>
              <div className="col-span-4">
                <div className="font-semibold">{jourLong(d.date)}</div>
                <div className="text-xs text-[#868E96] font-mono">{d.date}</div>
              </div>
              <div className="col-span-3">{TYPE_LABELS[d.type] || d.type}</div>
              <div className="col-span-3 font-mono text-xs">
                {d.horaire_debut ? `${d.horaire_debut} → ${d.horaire_fin}` : "—"}
              </div>
              <div className="col-span-2 text-right font-bold">{d.heures > 0 ? `${d.heures.toFixed(2)} h` : "—"}</div>
            </div>
          )) : (
            <div className="p-8 text-center text-sm text-[#868E96]">Aucune entrée de planning pour ce mois.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function BreakBox({ icon: Icon, label, value, unit }) {
  return (
    <div className="bg-white border border-[#DEE2E6] p-4">
      <Icon className="h-4 w-4 text-[#002FA7] mb-2" />
      <div className="font-display text-2xl font-black tracking-tight">
        {value}<span className="text-sm ml-1 text-[#868E96] font-normal">{unit}</span>
      </div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#868E96] font-semibold mt-1">{label}</div>
    </div>
  );
}
